// src/pdf-calcs/hltt-calculations.js
// Heavy Lift Tow Truck (HLTT) Mass Calculator logic. No DOM or jQuery dependencies.

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HLTT_KEYS = [
   'steerAxleTareMass',
   'actualSteerAxleMass',
   'wheelbase',
   'rearAxleTareMass',
   'maximumRearAxleLoad',
   'actualRearAxleLoad',
   'couplingRearOverhang',
];

function validateInputs(input, requiredKeys) {
   for (const key of requiredKeys) {
      const val = input[key];
      if (val === undefined || val === null || val === '') {
         return { ok: false, error: `Invalid input: ${key} is required.` };
      }
      if (typeof val !== 'number' || !Number.isFinite(val)) {
         return { ok: false, error: `Invalid input: ${key} must be a finite number.` };
      }
   }
   return { ok: true };
}

function failResult(error, inputs) {
   return { ok: false, error, inputs };
}

// ---------------------------------------------------------------------------
// HLTT Mass Calculator
// ---------------------------------------------------------------------------

/**
 * Calculate the HLTT Mass values from input fields.
 *
 * @param {object} input
 * @param {number} input.steerAxleTareMass       - T  (t)
 * @param {number} input.actualSteerAxleMass     - SM (t)
 * @param {number} input.wheelbase               - WB (m)
 * @param {number} input.rearAxleTareMass        - R  (t)
 * @param {number} input.maximumRearAxleLoad     - D  (t)
 * @param {number} input.actualRearAxleLoad      - DM (t)
 * @param {number} input.couplingRearOverhang    - ROH (m)
 *
 * @returns {{ ok: true, inputs, outputs, intermediate } | { ok: false, error, inputs }}
 */
export function calculateHlttMass(input) {
   const v = validateInputs(input, HLTT_KEYS);
   if (!v.ok) return failResult(v.error, input);

   const {
      steerAxleTareMass,
      actualSteerAxleMass,
      wheelbase,
      rearAxleTareMass,
      maximumRearAxleLoad,
      actualRearAxleLoad,
      couplingRearOverhang,
   } = input;

   if (wheelbase <= 0) return failResult('Wheelbase must be greater than zero.', input);
   if (couplingRearOverhang <= 0) return failResult('Coupling rear overhang must be greater than zero.', input);

   const steerRearSum = actualSteerAxleMass + actualRearAxleLoad;
   if (steerRearSum <= 0) {
      return failResult('Actual steer axle mass + actual rear axle load must be greater than zero.', input);
   }

   const rohPlusWb = couplingRearOverhang + wheelbase;

   if (maximumRearAxleLoad < rearAxleTareMass) {
      return failResult('Maximum rear axle load must be greater than or equal to rear axle tare mass.', input);
   }

   // Steer Axle % = (SM / (SM + DM)) × 100
   const steerAxlePercent = (actualSteerAxleMass / steerRearSum) * 100;

   // Safe Work Load (SWLM1) = (0.6 × T × WB) / ROH
   const safeWorkLoad = (0.6 * steerAxleTareMass * wheelbase) / couplingRearOverhang;

   // Compliant Tow Load (SWLM2) = ((D − R) × WB) / (ROH + WB)
   const compliantTowLoad = ((maximumRearAxleLoad - rearAxleTareMass) * wheelbase) / rohPlusWb;

   // Maximum Load = min(SWL, CTL)
   const maximumLoad = Math.min(safeWorkLoad, compliantTowLoad);

   if (
      !Number.isFinite(steerAxlePercent) ||
      !Number.isFinite(safeWorkLoad) ||
      !Number.isFinite(compliantTowLoad) ||
      !Number.isFinite(maximumLoad)
   ) {
      return failResult('Calculation produced a non-finite result.', input);
   }

   return {
      ok: true,
      inputs: input,
      outputs: {
         steerAxlePercent,
         maximumLoad,
         safeWorkLoad,
         compliantTowLoad,
      },
      intermediate: {
         safeWorkLoad,
         compliantTowLoad,
      },
   };
}
